import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  computed,
  Component,
  DestroyRef,
  ElementRef,
  Inject,
  NgZone,
  OnInit,
  ViewChild,
  inject,
  signal,
  viewChild
} from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { FormsModule } from '@angular/forms';
import { catchError, distinctUntilChanged, filter, finalize, map, switchMap, tap } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { NgSelectModule } from '@ng-select/ng-select';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { SIDENAV_DATA } from '@shared/services/sidenav/sidenav.data';
import { SidenavService } from '@shared/services/sidenav/sidenav.service';
import { UIModule } from '@shared/modules/ui/ui.module';
import { ButtonGroupConfig } from '@shared/modules/ui/components/button-group-v2/button-group-v2.component';
import { SharedModule } from '@shared/shared.module';
import { FormsV2Module } from '@shared/modules/forms-v2/forms-v2.module';
import { ButtonSize, ButtonTypes } from '@shared/modules/ui/components/button/button.component';
import { TextAttachmentsConfig } from '@shared/components/text-attachments-input/models/text-attachments-config';
import { AttachmentManagerListV2Component } from '@shared/modules/attachment-manage/components/attachment-manager-list-v2/attachment-manager-list-v2.component';
import { AttachmentsManageModule } from '@shared/modules/attachment-manage/attachments-manage.module';
import { MailingQuillFormats } from '@modules/mailing-templates/shared/modules/mailing-sidenav/components/mailing-sidenav/data/mailing-quill-formats.data';
import { MailingQuillModules } from '@modules/mailing-templates/shared/modules/mailing-sidenav/components/mailing-sidenav/data/mailing-quill-modules.data';
import { ProjectStage } from '@shared/enums/project-stage.enum';
import { ObjectTaskType, TaskType } from '@shared/enums/task-type.enum';
import { TaskStatus } from '@shared/enums/task-status.enum';
import { TaskService } from '@shared/modules/tasks/services/task-service';
import { ListTaskEventType, ListTaskService } from '@shared/modules/list/services/list-task.service';
import { SnackBarService } from '@core/services/snackbar.service';
import { Task } from '@shared/models/task.model';
import { forkJoin, of } from 'rxjs';
import { Config } from '@shared/configs/config';
import { Contact } from '@modules/contacts/shared/models/contact.model';
import { Regex } from '@shared/configs/regex';
import { AssignedContactList } from '@shared/models/assigned-contact.model';
import { NgxMatTimepickerComponent, NgxMatTimepickerModule } from 'ngx-mat-timepicker';
import moment from 'moment';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmModalComponent } from '@shared/components/confirm-modal/confirm-modal.component';
import { ImageAttachmentModalComponent } from '@shared/components/image-attachment-modal/image-attachment-modal.component';
import { TaskSidenavController } from '@shared/modules/task-sidenav/controllers/task-sidenav.controller';
import { TaskSidenavService } from '@shared/modules/task-sidenav/services/task-sidenav.service';
import { TaskController } from '@shared/controllers/task.controller';
import { formatApiUtcDateTime } from '@shared/helpers/date-format.helper';
import { normalizeQuillHtmlForEmail } from '@shared/helpers/quill-email-content.helper';
import { TaskEmailVariant, TaskEmailVariantType } from '@shared/models/task.model';
import { AttachmentService } from '@shared/services/attachment.service';
import { AttachmentType } from '@shared/components/text-attachments-input/enums/text-attachment-type.enum';
import { getFileType } from '@shared/helpers/basic-file-type.helper';
import { TaskStatusList } from '@shared/consts/task-status.list';
import { ProjectAPIService } from '@modules/projects/shared/services/project-api.service';
import { Project } from '@modules/projects/shared/models/project.model';
import {
  buildAssignmentSignature,
  buildInitialFormState,
  EmailMapperToken,
  getSelectedEmailVariant,
  isProcessMessageLikeTaskType,
  normalizeAssignedValues,
  normalizeEmailMappersResponse,
  normalizeEmailVariantType,
  parseEmailList,
  ProcessMessageSidenavData,
  resolveAvailableEmailVariantType,
  resolveProcessMessageTaskType,
  syncEmailVariantsWithCurrentValues
} from './process-message-tasks-sidenav.helper';

@Component({
  selector: 'project-stage-process-message-sidenav',
  standalone: true,
  templateUrl: './process-message-task-sidenav.component.html',
  styleUrls: ['./process-message-task-sidenav.component.scss'],
  imports: [
    ReactiveFormsModule,
    FormsModule,
    TranslateModule,
    NgSelectModule,
    AngularSvgIconModule,
    UIModule,
    FormsV2Module,
    SharedModule,
    AttachmentsManageModule,
    NgxMatTimepickerModule
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ProcessMessageTaskSidenavComponent implements OnInit, AfterViewInit {
  private readonly taskSidenavController = new TaskSidenavController();
  private readonly sideService = inject(SidenavService);
  protected readonly taskSidenavService = inject(TaskSidenavService);
  private readonly fb = inject(FormBuilder);
  private readonly taskService = inject(TaskService);
  private readonly listTaskService = inject(ListTaskService);
  private readonly snackBarService = inject(SnackBarService);
  private readonly translateService = inject(TranslateService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);
  private readonly dialog = inject(MatDialog);
  private readonly zone = inject(NgZone);
  private readonly attachmentService = inject(AttachmentService);
  private readonly projectApiService = inject(ProjectAPIService);

  @ViewChild('editor') editor: any;
  @ViewChild('attachmentsList') attachmentsList?: AttachmentManagerListV2Component;
  @ViewChild('subjectWrapper', { read: ElementRef }) subjectWrapper?: ElementRef<HTMLElement>;

  protected readonly data = (this.sideData?.data || {}) as ProcessMessageSidenavData;
  protected readonly mode = signal<'create' | 'send' | 'edit'>('create');
  protected readonly taskDetails = signal<Task | null>(null);
  protected projectInitialValue: Project[] = [];
  protected showBccField = signal<boolean>(false);
  protected showCcField = signal<boolean>(false);
  protected readonly selectedEmailVariantType = signal<TaskEmailVariantType>(TaskEmailVariantType.COURTESY);
  protected readonly savingEmailVariantPreference = signal<boolean>(false);

  protected readonly ButtonSize = ButtonSize;
  protected readonly ButtonTypes = ButtonTypes;
  protected readonly TaskStatus = TaskStatus;
  protected readonly formats = MailingQuillFormats;
  protected clipboardFileName = '';
  protected readonly modules = {
    ...MailingQuillModules(),
    imageHandler: {
      upload: (file) => {
        return new Promise((resolve, reject) => {
          this.zone.run(() => {
            this.dialog
              .open(ImageAttachmentModalComponent, { width: Config.CONFIRM_MODAL_WIDTH })
              .afterClosed()
              .pipe(
                tap((data) => {
                  if ((data?.asAttachment && !data?.asImage) || !data) {
                    this.clearImageFromQuillAtIndex();
                  }
                  if (data?.asAttachment) this.onAttachmentAdded(file);
                  if (data?.asImage) return this.uploadFile(file, { resolve, reject });
                })
              )
              .subscribe();
          });
        });
      },
      accepts: ['*']
    },
    imageDropAndPaste: {
      handler: this.uploadImage.bind(this)
    }
  };
  protected variableTokens: EmailMapperToken[] = [];
  protected initialEmails: any[] = [];
  protected assignedToInitialValue: any[] = [];
  private readonly assignedContactList = new AssignedContactList();
  protected today = new Date();
  private lastSavedAssignmentSignature = '';
  private readonly closingSidenav = signal(false);
  private readonly pendingHeaderStatus = signal<TaskStatus | null>(null);

  protected timepicker = viewChild<NgxMatTimepickerComponent>('picker');

  protected readonly attachmentsConfig: TextAttachmentsConfig = {
    fileAttachments: true,
    photoAttachments: true,
    videoAttachments: true,
    maxAttachmentSize: 50
  };

  protected saving = false;
  protected loadingVariables = false;
  protected activeField: 'subject' | 'content' = 'content';
  protected subjectCaretStart = 0;
  protected subjectCaretEnd = 0;
  protected quillCaretIndex: number | null = null;

  protected readonly form = this.fb.group({
    templateName: ['', Validators.required],
    projectId: [null as number | null],
    projectName: [''],
    to: [null as string | null, Validators.required],
    bcc: [[] as string[]],
    cc: [[] as string[]],
    subject: ['', Validators.required],
    content: ['', Validators.required],
    assignedToIds: [[] as number[]],
    assignedEmails: [[] as string[]],
    sendDate: [null],
    sendTime: [null]
  });

  protected readonly taskStatusOptions = TaskStatusList.map((item) => ({
    ...item,
    label: this.translateService.instant(item.label)
  }));

  protected get contactsUrl(): string {
    return `${Config.API}/contact/search?emailsOnly=1`;
  }

  protected get attachmentUploadConfig(): { id: number; objectType: string } | null {
    const taskId = Number(this.taskDetails()?.id || this.data?.task?.id || 0);
    return taskId ? { id: taskId, objectType: 'task' } : null;
  }

  protected get taskAttachments(): any[] {
    const task = this.taskDetails() || this.data?.task;
    return task?.originalAndChangeAttachments || task?.attachments || [];
  }

  protected get parentTask(): Task | null {
    return (this.taskDetails()?.parent as Task) || (this.data?.task?.parent as Task) || null;
  }

  protected toggleWidth(): void {
    this.taskSidenavService.toggleExpandedWidth();
  }

  protected get shouldShowParentLink(): boolean {
    return isProcessMessageLikeTaskType(this.getProcessMessageTaskType()) && !!this.parentTask?.description;
  }

  protected get emailVariants(): TaskEmailVariant[] {
    const task = this.taskDetails() || this.data?.task;
    return Array.isArray(task?.emailVariants) ? task.emailVariants : [];
  }

  protected get shouldShowEmailVariantSwitch(): boolean {
    return this.emailVariants.length > 1;
  }

  protected get isDirectEmailVariant(): boolean {
    return this.selectedEmailVariantType() === TaskEmailVariantType.DIRECT;
  }

  protected get emailVariantButtonGroupConfig(): ButtonGroupConfig {
    const selectedType = this.selectedEmailVariantType();

    return {
      buttons: [
        {
          name: this.translateService.instant(
            'ProjectsDashboard.stageTasks.messageTaskSidenav.directVariant'
          ),
          active: selectedType === TaskEmailVariantType.DIRECT,
          prop: TaskEmailVariantType.DIRECT
        },
        {
          name: this.translateService.instant(
            'ProjectsDashboard.stageTasks.messageTaskSidenav.courtesyVariant'
          ),
          active: selectedType === TaskEmailVariantType.COURTESY,
          prop: TaskEmailVariantType.COURTESY
        }
      ]
    };
  }

  protected get currentTaskStatus(): TaskStatus {
    return this.currentTaskStatusState();
  }

  protected get currentTaskStatusClass(): string {
    return `select-colored-${this.currentTaskStatus}`;
  }

  private readonly currentTaskStatusState = computed(
    () =>
      (this.pendingHeaderStatus() ||
        this.taskDetails()?.status ||
        this.data?.task?.status ||
        TaskStatus.CREATED) as TaskStatus
  );

  constructor(@Inject(SIDENAV_DATA) private readonly sideData: SIDENAV_DATA) {}

  ngOnInit(): void {
    const initialMode = this.data?.mode;
    this.mode.set(initialMode === 'send' || initialMode === 'edit' ? initialMode : 'create');
    this.updateFormValidatorsForMode();
    this.initFormForMode();
    this.initAssignedToAutosave();
    this.loadExistingTaskDetails();
    this.loadEmailMappers();
  }

  ngAfterViewInit(): void {
    this.initEditorCustomMatcher();
  }

  protected close(): void {
    this.closingSidenav.set(true);
    this.sideService.close();
  }

  protected openParentPreview(): void {
    const parentTask = this.parentTask;
    if (!parentTask?.id) return;

    const taskType = String(parentTask?.type || '');
    if (isProcessMessageLikeTaskType(taskType)) {
      this.sideService.open(
        ProcessMessageTaskSidenavComponent,
        this.taskSidenavController.getWideSidenavConfig(),
        {
          mode: 'send',
          projectId: parentTask?.project?.id || this.data?.projectId,
          stage: (parentTask?.project?.stage || this.data?.stage) as ProjectStage,
          sortOrder: Number(parentTask?.sortOrder || 1),
          parentId: parentTask?.parentId ?? null,
          groupId: parentTask?.taskGroup?.id ?? this.data?.groupId ?? null,
          mapperTaskId: parentTask.id,
          taskType,
          task: parentTask
        }
      );
      return;
    }

    this.taskSidenavController.previewTask(String(parentTask.id), {
      type: parentTask?.type || TaskType.TYPE_TASK,
      isProcessTaskContext: true,
      groupType: 'activity-template',
      activityTemplateTaskType: parentTask?.type || TaskType.TYPE_TASK,
      groupId: parentTask?.taskGroup?.id ?? this.data?.groupId ?? null
    });
  }

  protected save(): void {
    if (this.isEditMode()) {
      this.confirmEditSave();
      return;
    }

    if (this.isSendMode()) {
      this.sendMail();
      return;
    }

    this.form.markAllAsTouched();
    this.form.updateValueAndValidity();
    if (this.form.invalid || this.saving || !this.data?.projectId || !this.data?.stage) {
      this.cdr.markForCheck();
      return;
    }

    const assignedRaw = this.form.get('assignedToIds')?.value || [];
    const { assignedToIds, assignedEmails } = normalizeAssignedValues(assignedRaw);

    const templateNameValue = (this.form.get('templateName')?.value || '').trim();
    const subjectValue = this.getMessageSubjectValue();
    const contentValue = this.getMessageContentValue();
    const emailContentValue = normalizeQuillHtmlForEmail(contentValue);

    const selectedProjectId = Number(this.form.get('projectId')?.value || this.data.projectId || 0);
    if (!selectedProjectId) {
      this.cdr.markForCheck();
      return;
    }

    const payload = {
      projectId: selectedProjectId,
      groupId: this.data.groupId ?? null,
      objectType: ObjectTaskType.PROJECT,
      description: templateNameValue,
      type: this.getProcessMessageTaskType(),
      content: contentValue,
      emailTitle: subjectValue,
      emailContent: emailContentValue,
      quillContent: null,
      price: 0,
      isPriceEstimated: false,
      discount: 0,
      additionalTime: 0,
      isAdditionalTimeEstimated: false,
      hiddenForClient: true,
      isHighPriority: false,
      internalNote: subjectValue,
      tags: [],
      assignedToIds: Array.isArray(assignedToIds) ? assignedToIds : [],
      assignedEmails: Array.isArray(assignedEmails) ? assignedEmails : [],
      emailTemplateId: null,
      attachmentPacketId: this.attachmentsList?.service?.attachmentPacket?.id || null,
      attachments: [],
      dataChanges: 1,
      status: TaskStatus.CREATED,
      stage: this.data.stage,
      order: this.data.sortOrder ?? 1,
      sortOrder: this.data.sortOrder ?? 1,
      parentId: this.data.parentId ?? null,
      editMode: 'PROJECT'
    };

    this.saving = true;
    this.cdr.markForCheck();

    this.taskService
      .createProjectActivityTask(payload)
      .pipe(
        tap((task: Task) => {
          (task as Task & { _isActivityTemplate?: boolean })._isActivityTemplate = true;
          this.listTaskService.emitter.emit({ type: ListTaskEventType.ADD_TASK, data: task });
          this.snackBarService.success(this.translateService.instant('Tasks.messageCreated'));
          this.sideService.close();
        }),
        finalize(() => {
          this.saving = false;
          this.cdr.markForCheck();
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  protected get sidenavTitle(): string {
    if (this.isSendMode()) {
      return (
        (this.data?.task?.internalNote || this.data?.task?.description || '').trim() ||
        this.translateService.instant('ProjectsDashboard.stageTasks.messageTaskSidenav.sendTitle')
      );
    }
    if (this.isEditMode()) {
      return this.translateService.instant('ProjectsDashboard.stageTasks.messageTaskSidenav.editTitle');
    }
    return this.translateService.instant('ProjectsDashboard.stageTasks.messageTaskSidenav.title');
  }

  protected isSendMode(): boolean {
    return this.mode() === 'send' && !!this.data?.task?.id;
  }

  protected isEditMode(): boolean {
    return this.mode() === 'edit' && !!this.data?.task?.id;
  }

  protected isCreateMode(): boolean {
    return this.mode() === 'create' || !this.data?.task?.id;
  }

  protected canShowHeaderActions(): boolean {
    return !!this.data?.task?.id;
  }

  protected switchToEditMode(): void {
    if (!this.data?.task?.id || this.mode() === 'edit') return;
    this.mode.set('edit');
    this.updateFormValidatorsForMode();
    this.initFormForMode();
    this.applySelectedEmailVariantToForm(false);
    this.loadEmailMappers();
    this.cdr.markForCheck();
  }

  protected deleteTask(): void {
    this.dialog
      .open(ConfirmModalComponent, {
        width: Config.CONFIRM_MODAL_WIDTH,
        autoFocus: false,
        data: {
          title: 'ProjectsDashboard.stageTasks.processTaskDeleteConfirmTitle',
          titleIcon: '/assets/img/ic-alert-circle.svg',
          body: 'ProjectsDashboard.stageTasks.processTaskDeleteConfirmBody',
          cancelButtonText: 'RemoveData.cancel',
          submitButtonText: 'RemoveData.confirm',
          submitButtonType: ButtonTypes.NEW_RED
        }
      })
      .afterClosed()
      .pipe(
        filter((confirm): confirm is boolean => !!confirm),
        tap(() => this.deleteTaskConfirmed()),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  protected onHeaderStatusChange(status: TaskStatus | string | null): void {
    const nextStatus = (status || TaskStatus.CREATED) as TaskStatus;
    const currentTask = this.taskDetails() || this.data?.task;
    if (!currentTask?.id || this.saving || currentTask.status === nextStatus) return;

    if (this.hasVisibleSubtasks(currentTask)) {
      this.pendingHeaderStatus.set(nextStatus);
      this.openSubtasksStatusConfirmation(nextStatus);
      return;
    }

    this.applyHeaderStatusChange(nextStatus, false);
  }

  private openSubtasksStatusConfirmation(nextStatus: TaskStatus): void {
    const { title, body } = this.getSubtasksStatusConfirmationContent(nextStatus);

    this.dialog
      .open(ConfirmModalComponent, {
        width: Config.CONFIRM_MODAL_WIDTH,
        data: {
          title,
          body,
          cancelButtonText: 'ProjectTasks.Task.cancelCompletedSubtasks',
          submitButtonText: 'ProjectTasks.Task.confirmCompletedSubtasks',
          submitButtonType: ButtonTypes.NEW_PRIMARY_BLUE
        }
      })
      .afterClosed()
      .subscribe((confirmed: boolean | undefined) => {
        if (confirmed !== true && confirmed !== false) {
          this.resetPendingHeaderStatus();
          return;
        }

        this.applyHeaderStatusChange(nextStatus, confirmed);
      });
  }

  private getSubtasksStatusConfirmationContent(status: TaskStatus): { title: string; body: string } {
    switch (status) {
      case TaskStatus.COMPLETED:
        return {
          title: 'ProjectTasks.Task.doYouWantToCompletedSubtasksTitle',
          body: 'ProjectTasks.Task.doYouWantToCompletedSubtasks'
        };
      case TaskStatus.CANCELLED:
        return {
          title: 'ProjectTasks.Task.doYouWantToCanceledSubtasksTitle',
          body: 'ProjectTasks.Task.doYouWantToCanceledSubtasks'
        };
      default:
        return {
          title: 'ProjectTasks.Task.doYouWantToCreatedSubtasksTitle',
          body: 'ProjectTasks.Task.doYouWantToCreatedSubtasks'
        };
    }
  }

  private applyHeaderStatusChange(nextStatus: TaskStatus, applyStatusToSubtasks: boolean): void {
    const currentTask = this.taskDetails() || this.data?.task;
    if (!currentTask?.id || this.saving) return;

    const task = new Task({
      ...currentTask,
      status: nextStatus
    } as Task);

    if (applyStatusToSubtasks) {
      this.applyStatusToTaskTree(task, nextStatus);
    }

    this.saving = true;
    this.cdr.markForCheck();

    this.syncSubtasksStatus(task, applyStatusToSubtasks)
      .pipe(
        switchMap(() =>
          new TaskController(task).changeTaskStatus(task, this.listTaskService.isMyWorkTaskLists)
        ),
        tap(() => {
          this.updateTaskDetailsState({ status: nextStatus });
          this.pendingHeaderStatus.set(null);

          if (applyStatusToSubtasks) {
            const nextTask = this.taskDetails();
            if (nextTask) {
              this.applyStatusToTaskTree(nextTask, nextStatus);
              this.updateCompletedChildrenCountRecursively(nextTask);
            }
          }

          this.listTaskService.emitter.emit({
            type: ListTaskEventType.UPDATE_TASK,
            data: { task: this.taskDetails() || this.data?.task, _isActivityTemplate: true }
          });
          this.snackBarService.success(
            this.translateService.instant('Tasks.successStatusChangeAction', {
              taskType: this.translateService.instant(`Tasks.TaskType.${task?.type || 'task'}`),
              action: this.translateService.instant(
                `Tasks.statusAction.${
                  nextStatus === TaskStatus.COMPLETED
                    ? 'completed'
                    : nextStatus === TaskStatus.CANCELLED
                      ? 'canceled'
                      : 'created'
                }`
              )
            })
          );
        }),
        catchError(() => {
          this.resetPendingHeaderStatus();
          this.snackBarService.error(this.translateService.instant('Tasks.errorStatusChange'));
          return of(null);
        }),
        finalize(() => {
          this.pendingHeaderStatus.set(null);
          this.saving = false;
          this.cdr.markForCheck();
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  private resetPendingHeaderStatus(): void {
    this.pendingHeaderStatus.set(null);
  }

  private syncSubtasksStatus(task: Task, applyStatusToSubtasks: boolean) {
    if (!applyStatusToSubtasks) return of(null);

    if (task?.status === TaskStatus.COMPLETED) {
      return new TaskController(task).completeSubtasks(task);
    }

    const subtasks = this.collectDescendantTasks(task);
    if (!subtasks.length) return of(null);

    return forkJoin(
      subtasks.map((subtask) => {
        const ctrl = new TaskController(subtask);
        return ctrl.changeTaskStatus(subtask, this.listTaskService.isMyWorkTaskLists);
      })
    );
  }

  private collectDescendantTasks(task: Task): Task[] {
    const descendants: Task[] = [];

    const collect = (parent: Task): void => {
      (parent?.children || []).forEach((child) => {
        descendants.push(child);
        collect(child);
      });
    };

    collect(task);
    return descendants;
  }

  private applyStatusToTaskTree(task: Task, status: TaskStatus): void {
    (task.children || []).forEach((child: Task) => {
      child.status = status;
      this.applyStatusToTaskTree(child, status);
    });
  }

  private updateCompletedChildrenCount(task: Task | null): void {
    if (!task) return;
    task.completedChildrenCount = (task.children || []).filter(
      (child: Task) => child.status === TaskStatus.COMPLETED && !child?.deleted
    ).length;
  }

  private updateCompletedChildrenCountRecursively(task: Task): void {
    (task.children || []).forEach((child: Task) => this.updateCompletedChildrenCountRecursively(child));
    this.updateCompletedChildrenCount(task);
  }

  private hasVisibleSubtasks(task: Task): boolean {
    return !!(task?.children || []).some((child) => !child?.deleted);
  }

  private deleteTaskConfirmed(): void {
    const taskId = Number(this.data?.task?.id || 0);
    if (!taskId || this.saving) return;

    this.saving = true;
    this.cdr.markForCheck();

    this.taskService
      .deleteTask(taskId)
      .pipe(
        tap(() => {
          this.snackBarService.success(this.translateService.instant('Tasks.messageDeleted'));
          this.listTaskService.emitter.emit({
            type: ListTaskEventType.REMOVE_TASK,
            data: { task: this.data?.task, closeSidenav: true, _isActivityTemplate: true }
          });
          this.closingSidenav.set(true);
          this.sideService.close();
        }),
        catchError(() => {
          this.snackBarService.error(this.translateService.instant('Tasks.errorDelete'));
          return of(null);
        }),
        finalize(() => {
          this.saving = false;
          this.cdr.markForCheck();
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  protected onSubjectFocus(event: FocusEvent): void {
    this.activeField = 'subject';
    this.updateSubjectCaret(event.target as HTMLInputElement | null);
  }

  protected onSubjectCaretChanged(event: Event): void {
    this.updateSubjectCaret(event.target as HTMLInputElement | null);
  }

  protected onEditorFocused(): void {
    this.activeField = 'content';
  }

  protected onEditorSelectionChanged(event: { range?: { index: number } | null }): void {
    if (event?.range && typeof event.range.index === 'number') {
      this.activeField = 'content';
      this.quillCaretIndex = event.range.index;
    }
  }

  protected preventVariableBlur(event: MouseEvent): void {
    event.preventDefault();
  }

  protected addVariable(token: EmailMapperToken): void {
    const variable = token?.value || token?.label || '';
    if (!variable) return;

    if (this.activeField === 'subject') {
      this.insertVariableIntoSubject(variable);
      return;
    }

    const quill = this.editor?.quillEditor;
    if (!quill) {
      const current = this.form.get('content')?.value || '';
      this.form.get('content')?.setValue(`${current} ${variable}`.trim());
      return;
    }

    const selection = quill.getSelection();
    const fallbackIndex = typeof this.quillCaretIndex === 'number' ? this.quillCaretIndex : quill.getLength();
    const index = selection ? selection.index : fallbackIndex;
    quill.insertText(index, variable, 'user');
    const nextCaret = index + variable.length;
    this.quillCaretIndex = nextCaret;

    const focusAndSetCursor = () => {
      quill.focus();
      quill.setSelection(nextCaret, 0, 'silent');
    };

    focusAndSetCursor();
    requestAnimationFrame(focusAndSetCursor);
    setTimeout(focusAndSetCursor, 0);
    setTimeout(focusAndSetCursor, 16);
  }

  protected onEmailVariantToggle(isDirect: boolean): void {
    const nextType = isDirect ? TaskEmailVariantType.DIRECT : TaskEmailVariantType.COURTESY;
    this.onEmailVariantTypeChange(nextType);
  }

  protected onEmailVariantTypeChange(type: TaskEmailVariantType): void {
    if (this.savingEmailVariantPreference()) return;

    const nextType = normalizeEmailVariantType(type) || TaskEmailVariantType.COURTESY;
    const previousType = this.selectedEmailVariantType();

    if (nextType === previousType) return;

    this.syncCurrentFormIntoSelectedVariant();
    this.selectedEmailVariantType.set(resolveAvailableEmailVariantType(this.emailVariants, nextType));
    this.applySelectedEmailVariantToForm();
    this.saveEmailVariantPreference(previousType);
  }

  protected uploadImage(imageDataUrl, type, imageData): void {
    const quill = this.editor?.quillEditor;
    if (!quill) return;

    const selection = quill.getSelection();
    const insertIndex = selection ? selection.index : quill.getLength();

    this.clearImageFromQuillAtIndex();

    this.zone.run(() => {
      this.dialog
        .open(ImageAttachmentModalComponent, { width: Config.CONFIRM_MODAL_WIDTH })
        .afterClosed()
        .subscribe((data) => {
          const file = imageData.toFile(this.clipboardFileName || imageData.name);

          if (data?.asAttachment) this.onAttachmentAdded(file);

          if (data?.asImage) {
            this.uploadFile(file, {
              resolve: (url) => {
                quill.insertEmbed(insertIndex - 1, 'image', url, 'user');
                setTimeout(() => quill.setSelection(insertIndex, 0));
              }
            });
          }

          this.clipboardFileName = '';
        });
    });
  }

  protected uploadFile(
    file: File,
    { resolve, reject }: { resolve?: (url: string) => void; reject?: (reason?: any) => void } = {}
  ): void {
    if (getFileType(file) === AttachmentType.FILE) {
      this.snackBarService.error(this.translateService.instant('ProjectCreator.imagesAllowed'));
      reject?.('Unsupported');
      return;
    }

    this.attachmentService
      .uploadAttachment({
        objectId: `id-${file.name}`,
        objectType: 'wysiwyg-content',
        files: [file]
      })
      .subscribe({
        next: ([data]) => {
          const url = `${Config.PRIVATE_ATTACHMENT}${data.file}`;
          resolve?.(url);
        },
        error: (err) => {
          console.error('File upload failed:', err);
          reject?.('Upload failed');
        }
      });
  }

  protected onAttachmentAdded(attachment): void {
    this.attachmentsList?.onFilesChange([attachment]);
  }

  protected clearImageFromQuillAtIndex(): void {
    const quill = this.editor?.quillEditor;
    if (!quill) return;

    const selection = quill.getSelection();
    const index = selection ? selection.index : quill.getLength() - 1;

    const [blot] = quill.getLeaf(index);
    if (!blot) return;

    const node = blot.domNode as HTMLElement;

    if (node.tagName === 'IMG' && node.getAttribute('src')?.startsWith('data:')) {
      quill.deleteText(index - 1, 1, 'user');
    }

    const progressBarEl = quill.container.parentElement?.querySelector('.quill-progress-wrapper');
    progressBarEl?.remove();
  }

  protected initEditorCustomMatcher(): void {
    document.querySelector('.quill-custom-matcher')?.addEventListener('paste', (e: ClipboardEvent) => {
      const clipboardData = e.clipboardData || (window as any).clipboardData;
      const isImage = clipboardData.types.length && clipboardData.types.includes('Files');
      if (!isImage) return;
      const file = clipboardData.files[0];
      this.clipboardFileName = file.name;
    });
  }

  private sendMail(): void {
    const taskId = Number(this.taskDetails()?.id || this.data?.task?.id || 0);
    if (!taskId || this.saving) return;

    const to = String(this.form.get('to')?.value || '').trim();
    if (!to) {
      this.form.get('to')?.markAsTouched();
      this.cdr.markForCheck();
      return;
    }

    const bcc = parseEmailList(this.form.get('bcc')?.value);
    const cc = parseEmailList(this.form.get('cc')?.value);
    const subject = this.getMessageSubjectValue();
    const content = this.getMessageContentValue();
    const emailContent = normalizeQuillHtmlForEmail(content);
    const assignedRaw = this.form.get('assignedToIds')?.value || [];
    const { assignedToIds, assignedEmails } = normalizeAssignedValues(assignedRaw);

    const sendDate = this.form.get('sendDate')?.value;
    const sendTime = this.form.get('sendTime')?.value;
    let scheduledAt: string | null = null;

    if (sendDate && sendTime) {
      const [hours, minutes] = String(sendTime).split(':').map(Number);
      const m = moment(sendDate).set({ hour: hours, minute: minutes, second: 0 });
      scheduledAt = m.isValid() ? formatApiUtcDateTime(m) : null;
    }

    this.saving = true;
    this.cdr.markForCheck();

    this.taskService
      .editPatchTask(
        {
          content,
          emailTitle: subject,
          emailContent,
          emailVariants: this.buildEmailVariantsPayload(),
          editMode: 'PROJECT',
          assignedToIds,
          assignedEmails,
          attachmentPacketId: this.attachmentsList?.service?.attachmentPacket?.id || null,
          attachments: []
        },
        taskId
      )
      .pipe(
        switchMap(() => this.taskService.sendTaskMail(taskId, { to, bcc, cc, scheduledAt })),
        tap(() => {
          const currentTask = this.data.task as Task;
          if (currentTask) {
            currentTask.content = content;
            currentTask.assignedEmails = assignedEmails;
            this.listTaskService.emitter.emit(
              this.listTaskService.isMyWorkTaskLists
                ? {
                    type: ListTaskEventType.REMOVE_TASK,
                    data: { task: currentTask }
                  }
                : {
                    type: ListTaskEventType.UPDATE_TASK,
                    data: { task: currentTask, _isActivityTemplate: true }
                  }
            );
          }
          this.snackBarService.success(
            this.translateService.instant('ProjectsDashboard.stageTasks.messageTaskSidenav.sendSuccess')
          );
          this.closingSidenav.set(true);
          this.sideService.close();
        }),
        catchError(() => {
          this.snackBarService.error(
            this.translateService.instant('ProjectsDashboard.stageTasks.messageTaskSidenav.sendError')
          );
          return of(null);
        }),
        finalize(() => {
          this.saving = false;
          this.cdr.markForCheck();
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  private initAssignedToAutosave(): void {
    const assignedControl = this.form.get('assignedToIds');
    if (!assignedControl) return;

    assignedControl.valueChanges
      .pipe(
        filter(() => this.isSendMode()),
        filter(() => !this.closingSidenav()),
        filter(() => !!Number(this.taskDetails()?.id || this.data?.task?.id || 0)),
        map((assignedValue) => {
          const { assignedToIds, assignedEmails } = normalizeAssignedValues(assignedValue);

          return {
            assignedToIds,
            assignedEmails,
            signature: buildAssignmentSignature(assignedToIds, assignedEmails)
          };
        }),
        distinctUntilChanged((prev, curr) => prev.signature === curr.signature),
        filter(({ signature }) => signature !== this.lastSavedAssignmentSignature),
        switchMap(({ assignedToIds, assignedEmails, signature }) => {
          const taskId = Number(this.taskDetails()?.id || this.data?.task?.id || 0);

          return this.taskService.editPatchTask({ assignedToIds, assignedEmails }, taskId).pipe(
            tap((updatedTask: Task) => {
              this.lastSavedAssignmentSignature = signature;
              const currentTask = this.data.task as Task;
              if (currentTask) {
                currentTask.assignedEmails = assignedEmails;
              }

              this.listTaskService.emitter.emit({
                type: ListTaskEventType.UPDATE_TASK,
                data: { task: updatedTask || currentTask, _isActivityTemplate: true }
              });
            }),
            catchError(() => {
              this.snackBarService.error(this.translateService.instant('Tasks.errorTaskUpdate'));
              return of(null);
            })
          );
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  private saveEdit(): void {
    const taskId = Number(this.taskDetails()?.id || this.data?.task?.id || 0);
    if (!taskId || this.saving) return;

    this.form.markAllAsTouched();
    this.form.updateValueAndValidity();
    if (this.form.invalid) {
      this.cdr.markForCheck();
      return;
    }

    const assignedRaw = this.form.get('assignedToIds')?.value || [];
    const { assignedToIds, assignedEmails } = normalizeAssignedValues(assignedRaw);

    const subjectValue = this.getMessageSubjectValue();
    const contentValue = this.getMessageContentValue();
    const emailContentValue = normalizeQuillHtmlForEmail(contentValue);

    const patchPayload = {
      projectId: Number(this.form.get('projectId')?.value || this.data.projectId || 0) || undefined,
      description: (this.form.get('templateName')?.value || '').trim(),
      content: contentValue,
      emailTitle: subjectValue,
      emailContent: emailContentValue,
      emailVariants: this.buildEmailVariantsPayload(),
      internalNote: subjectValue,
      editMode: 'COMPANY',
      assignedToIds,
      assignedEmails,
      attachmentPacketId: this.attachmentsList?.service?.attachmentPacket?.id || null,
      attachments: [],
      status: (this.taskDetails()?.status || this.data?.task?.status || TaskStatus.CREATED) as TaskStatus
    };

    this.saving = true;
    this.cdr.markForCheck();

    this.taskService
      .editPatchTask(patchPayload, taskId)
      .pipe(
        tap((updatedTask: Task) => {
          this.listTaskService.emitter.emit({
            type: ListTaskEventType.UPDATE_TASK,
            data: { task: updatedTask || this.data?.task, _isActivityTemplate: true }
          });
          this.snackBarService.success(this.translateService.instant('Tasks.messageChanged'));
          this.closingSidenav.set(true);
          this.sideService.close();
        }),
        catchError(() => {
          this.snackBarService.error(this.translateService.instant('Tasks.errorTaskUpdate'));
          return of(null);
        }),
        finalize(() => {
          this.saving = false;
          this.cdr.markForCheck();
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  private confirmEditSave(): void {
    this.dialog
      .open(ConfirmModalComponent, {
        width: Config.CONFIRM_MODAL_WIDTH,
        data: {
          title: 'ProjectsDashboard.stageTasks.messageTaskSidenav.editConfirmTitle',
          body: 'ProjectsDashboard.stageTasks.messageTaskSidenav.editConfirmBody',
          submitButtonType: ButtonTypes.NEW_PRIMARY_BLUE
        }
      })
      .afterClosed()
      .pipe(
        filter((confirm): confirm is boolean => !!confirm),
        tap(() => this.saveEdit()),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  private loadEmailMappers(): void {
    if (this.isSendMode()) {
      this.variableTokens = [];
      return;
    }

    const mapperTaskId = Number(
      this.taskDetails()?.id || this.data?.mapperTaskId || this.data?.task?.id || 0
    );
    if (!mapperTaskId) {
      this.variableTokens = [];
      return;
    }

    this.loadingVariables = true;
    this.cdr.markForCheck();

    this.taskService
      .getTaskEmailMappers(mapperTaskId)
      .pipe(
        tap((response) => {
          this.variableTokens = normalizeEmailMappersResponse(response);
        }),
        finalize(() => {
          this.loadingVariables = false;
          this.cdr.markForCheck();
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  private initFormForMode(): void {
    if (this.isCreateMode()) {
      this.form.patchValue(
        {
          projectId: this.data?.projectId || null
        },
        { emitEvent: false }
      );
      this.loadCurrentProjectInitialValue();
      return;
    }

    const task = this.taskDetails();
    if (!task) {
      return;
    }

    const initialState = buildInitialFormState({
      task,
      projectId: this.data?.projectId || 0,
      isEditMode: this.isEditMode(),
      isSendMode: this.isSendMode(),
      mapAssignedUser: (user) => this.assignedContactList.createTagFromUserPerson(user),
      mapAssignedEmail: (email) => this.assignedContactList.createCustomTag(email)
    });
    this.assignedToInitialValue = initialState.assignedToInitialValue;

    this.form.patchValue(
      {
        templateName: initialState.templateName,
        projectId: initialState.projectId,
        projectName: initialState.projectName,
        to: initialState.to,
        bcc: initialState.bcc,
        subject: initialState.subject,
        content: initialState.content,
        assignedToIds: initialState.assignedToIds,
        assignedEmails: initialState.assignedEmails
      },
      { emitEvent: false }
    );

    this.lastSavedAssignmentSignature = initialState.assignmentSignature;
    this.initialEmails = initialState.initialEmails;
  }

  private loadCurrentProjectInitialValue(): void {
    const projectId = Number(this.data?.projectId || 0);
    if (!projectId) {
      this.projectInitialValue = [];
      return;
    }

    this.projectApiService
      .getProject(projectId)
      .pipe(
        tap((project: Project) => {
          this.projectInitialValue = project ? [project] : [];
          this.form.patchValue(
            {
              projectId: project?.id || projectId,
              projectName: project?.name || ''
            },
            { emitEvent: false }
          );
          this.cdr.markForCheck();
        }),
        catchError(() => {
          this.projectInitialValue = [];
          this.form.patchValue(
            {
              projectId
            },
            { emitEvent: false }
          );
          this.cdr.markForCheck();
          return of(null);
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  private updateFormValidatorsForMode(): void {
    const templateNameControl = this.form.get('templateName');
    const toControl = this.form.get('to');

    if (this.isSendMode()) {
      templateNameControl?.clearValidators();
      toControl?.setValidators([Validators.required]);
    } else {
      templateNameControl?.setValidators([Validators.required]);
      toControl?.clearValidators();
    }

    templateNameControl?.updateValueAndValidity({ emitEvent: false });
    toControl?.updateValueAndValidity({ emitEvent: false });
  }

  private loadExistingTaskDetails(): void {
    const taskId = Number(this.data?.task?.id || 0);
    if (!taskId) return;

    const request$ = this.taskService.getTask(taskId);
    if (!request$) return;

    request$
      .pipe(
        tap((task: Task) => {
          this.taskDetails.set(task);
          this.data.task = task;
          this.initFormForMode();
          this.loadEmailVariantPreference();
          this.loadEmailMappers();
          this.cdr.markForCheck();
        }),
        catchError(() => of(null)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  private getProcessMessageTaskType(): TaskType | string {
    return resolveProcessMessageTaskType([
      this.taskDetails()?.type,
      this.data?.task?.type,
      this.data?.taskType
    ]);
  }

  private getMessageSubjectValue(): string {
    const input = this.resolveSubjectInput();
    const raw = input?.value ?? this.form.get('subject')?.value ?? '';
    return String(raw).trim();
  }

  private getMessageContentValue(): string {
    const quillHtml = this.editor?.quillEditor?.root?.innerHTML;
    const formValue = this.form.get('content')?.value;
    return String(quillHtml ?? formValue ?? '');
  }

  private loadEmailVariantPreference(): void {
    if (!this.shouldShowEmailVariantSwitch) {
      return;
    }

    const projectId = Number(this.taskDetails()?.project?.id || this.data?.projectId || 0);
    if (!projectId) {
      this.selectedEmailVariantType.set(
        resolveAvailableEmailVariantType(this.emailVariants, TaskEmailVariantType.COURTESY)
      );
      this.applySelectedEmailVariantToForm(false);
      return;
    }

    this.savingEmailVariantPreference.set(true);

    this.taskService
      .getProjectEmailVariantPreference(projectId)
      .pipe(
        tap((response) => {
          const preferredType = normalizeEmailVariantType(response?.emailVariantType);
          this.selectedEmailVariantType.set(
            resolveAvailableEmailVariantType(
              this.emailVariants,
              preferredType || TaskEmailVariantType.COURTESY
            )
          );
          this.applySelectedEmailVariantToForm(false);
        }),
        catchError(() => {
          this.selectedEmailVariantType.set(
            resolveAvailableEmailVariantType(this.emailVariants, TaskEmailVariantType.COURTESY)
          );
          this.applySelectedEmailVariantToForm(false);
          return of(null);
        }),
        finalize(() => {
          this.savingEmailVariantPreference.set(false);
          this.cdr.markForCheck();
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  private saveEmailVariantPreference(previousType: TaskEmailVariantType): void {
    const projectId = Number(this.taskDetails()?.project?.id || this.data?.projectId || 0);
    if (!projectId) return;

    const nextType = this.selectedEmailVariantType();
    this.savingEmailVariantPreference.set(true);

    this.taskService
      .updateProjectEmailVariantPreference(projectId, nextType)
      .pipe(
        catchError(() => {
          this.selectedEmailVariantType.set(previousType);
          this.applySelectedEmailVariantToForm(false);
          return of(null);
        }),
        finalize(() => {
          this.savingEmailVariantPreference.set(false);
          this.cdr.markForCheck();
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  private applySelectedEmailVariantToForm(markDirty: boolean = true): void {
    const variant = getSelectedEmailVariant(this.emailVariants, this.selectedEmailVariantType());
    if (!variant) return;

    this.form.patchValue(
      {
        subject: variant.title || '',
        content: variant.content || ''
      },
      { emitEvent: false }
    );

    if (markDirty) {
      this.form.get('subject')?.markAsDirty();
      this.form.get('content')?.markAsDirty();
    } else {
      this.form.get('subject')?.markAsPristine();
      this.form.get('content')?.markAsPristine();
    }
  }

  private syncCurrentFormIntoSelectedVariant(): void {
    if (!this.emailVariants.length) return;
    const nextVariants = syncEmailVariantsWithCurrentValues(
      this.emailVariants,
      this.selectedEmailVariantType(),
      this.getMessageSubjectValue(),
      this.getMessageContentValue()
    );

    this.updateTaskDetailsState({ emailVariants: nextVariants });
  }

  private buildEmailVariantsPayload(): TaskEmailVariant[] | undefined {
    if (!this.emailVariants.length) return undefined;

    this.syncCurrentFormIntoSelectedVariant();
    return [...this.emailVariants];
  }

  private updateTaskDetailsState(partial: Partial<Task>): void {
    const currentTask = this.taskDetails() || this.data?.task;
    if (!currentTask) return;

    const nextTask = new Task({
      ...currentTask,
      ...partial
    } as Task);

    this.taskDetails.set(nextTask);
    this.data.task = nextTask;
  }

  private updateSubjectCaret(input: HTMLInputElement | null): void {
    const resolvedInput = this.resolveSubjectInput(input);
    if (!resolvedInput) return;
    this.subjectCaretStart = resolvedInput.selectionStart ?? resolvedInput.value.length ?? 0;
    this.subjectCaretEnd = resolvedInput.selectionEnd ?? this.subjectCaretStart;
  }

  private insertVariableIntoSubject(variable: string): void {
    const subjectCtrl = this.form.get('subject');
    if (!subjectCtrl) return;

    const currentValue = String(subjectCtrl.value || '');
    const start = Math.max(0, this.subjectCaretStart);
    const end = Math.max(start, this.subjectCaretEnd);
    const nextValue = `${currentValue.slice(0, start)}${variable}${currentValue.slice(end)}`;
    const nextCaret = start + variable.length;

    subjectCtrl.setValue(nextValue);
    this.subjectCaretStart = nextCaret;
    this.subjectCaretEnd = nextCaret;

    const input = this.resolveSubjectInput();
    if (input) {
      const focusAndSetCursor = () => {
        input.focus();
        input.setSelectionRange(nextCaret, nextCaret);
      };
      focusAndSetCursor();
      requestAnimationFrame(focusAndSetCursor);
      setTimeout(focusAndSetCursor, 0);
      setTimeout(focusAndSetCursor, 16);
    }
  }

  private resolveSubjectInput(input?: HTMLInputElement | null): HTMLInputElement | null {
    if (input && typeof input.selectionStart === 'number') return input;
    const candidate = this.subjectWrapper?.nativeElement?.querySelector('input') || null;
    return candidate instanceof HTMLInputElement ? candidate : null;
  }

  protected validateEmail(value: string): any {
    if (!value?.length) return null;
    if (!Regex.email.test(value.toString())) {
      this.snackBarService.error(this.translateService.instant('FormErrors.email'));
      return null;
    }
    return this.createEmailTag(value);
  }

  protected validateBccEmail(value: string): string | null {
    if (!value?.length) return null;
    const email = value.toString().trim();
    if (!Regex.email.test(email)) {
      this.snackBarService.error(this.translateService.instant('FormErrors.email'));
      return null;
    }
    return email;
  }

  protected successOnLoadContacts(
    response: { records?: Contact[] } | Contact[] | null | undefined
  ): Contact[] {
    const records = Array.isArray(response) ? response : response?.records || [];
    return records.map((contact) => new Contact(contact));
  }

  private createEmailTag(name: string): any {
    return { id: name, name, newItem: true, fullName: name, email: name };
  }

  protected toggleBccInput(): void {
    this.showBccField.set(!this.showBccField());
  }

  protected toggleCcInput(): void {
    this.showCcField.set(!this.showCcField());
  }

  protected onTimeSet(time: string): void {
    this.form.get('sendTime')?.setValue(time);
  }

  protected onDatepickerClose(): void {
    if (this.form.get('sendDate')?.value) this.timepicker()?.open();
  }
}
